import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { AuthProtectedRoutes } from "./Routes/ProtectedRoutes/AuthProtectedRoutes";
import { routeLinks } from "constants/paths";
import { UsersPage } from "pages/Admin/Users/UsersPage";
import { SomethingWentWrong } from "pages/Errors/500";
import { PageNotFound } from "pages/Errors/404";
import { EnquiryIndex } from "pages/ProjectManagement/Enquiry/Index";
import { ResetPasswordPage } from "pages/User/ResetPasswordPage";
import { TaskIndex } from "pages/TaskManagement/Index";
import { TaskManagementDashboard } from "pages/Dashboards/TaskManagement/TaskManagementDashboard";
import { LoginPage } from "pages/Auth/LoginPage";
import { SetPageAccessRoutes } from "Routes/ProtectedRoutes/SetPageAccessRoutes";
import { ProjectIndex } from "pages/ProjectManagement/Project/Index";
import { ProductIndex } from "pages/ProjectManagement/Product/Index";
import { TaskDashboardViewer } from "pages/Dashboards/TaskViewer/TaskDashboardViewer";
import { ProjectViewerDashboard } from "pages/Dashboards/ProjectViewer";
import { FinishIndex } from "pages/ProjectManagement/Finish/Index";
import { UserTaskPage } from "pages/Admin/Task/UserTaskPage";
import { Forbidden } from "pages/Errors/403";
import { SetPageAccessRoutesForProjectManagement } from "Routes/ProtectedRoutes/SetPageAccessRoutesForProjectManagement";
import { productOperationsList } from "pages/ProjectManagement/Product/constants/product.constants";
import { UnderMaintenance } from "pages/Errors/Maintenance";
import { CustomReportsIndex } from "pages/CustomReports";
import { AddNewTask } from "pages/Dashboards/TaskManagement/AddNewTask";
import { RnDPage } from "pages/Rnd/RnDPage";
import { TaskManagementDashboardOld } from "pages/Dashboards/TaskManagementOld/TaskManagementDashboard";

const App = () => {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const ref = useRef(null);

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      requestAnimationFrame(() => {
        setSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      });
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path={routeLinks.underMaintenance}
          element={<UnderMaintenance />}
        />
        <Route path="/rnd" element={<RnDPage />} />
        <Route path={routeLinks.authLogin} element={<LoginPage />} />
        <Route element={<AuthProtectedRoutes />}>
          <Route path={routeLinks.addNewTask} element={<AddNewTask />} />
          <Route
            path={routeLinks.viewerTaskDashboard}
            element={<TaskDashboardViewer />}
          />
          <Route
            path={routeLinks.viewerProjectTaskDashboard}
            element={<ProjectViewerDashboard />}
          />
          <Route
            path={routeLinks.authPasswordReset}
            element={<ResetPasswordPage />}
          />
          <Route element={<SetPageAccessRoutes />}>
            <Route>
              <Route
                path={routeLinks.dashboardCommon}
                element={<TaskManagementDashboard />}
              />
              <Route
                path={routeLinks.dashboardCommonOld}
                element={<TaskManagementDashboardOld />}
              />
            </Route>
            <Route path={routeLinks.adminUsers} element={<UsersPage />} />
            <Route path={routeLinks.adminUserTask} element={<UserTaskPage />} />
            <Route
              path={routeLinks.customReports}
              element={<CustomReportsIndex />}
            />
            <Route
              path={routeLinks.projectManagementEnquiry}
              element={<EnquiryIndex />}
            />
            <Route
              path={routeLinks.projectManagementFinish}
              element={<FinishIndex />}
            />
            <Route
              path={routeLinks.taskManagementTask}
              element={<TaskIndex />}
            />
          </Route>
          <Route element={<SetPageAccessRoutesForProjectManagement />}>
            <Route
              path={routeLinks.projectManagementProject}
              element={<ProjectIndex />}
            />
            <Route
              path={routeLinks.projectManagementProject + "/:projectId"}
              element={
                <ProductIndex
                  currentOperationPage={productOperationsList.listProduct}
                />
              }
            />
            <Route
              path={
                routeLinks.projectManagementProduct + "/:productLotId/update"
              }
              element={
                <ProductIndex
                  currentOperationPage={productOperationsList.updateProduct}
                />
              }
            />
            <Route
              path={routeLinks.projectManagementProduct + "/:projectId/add"}
              element={
                <ProductIndex
                  currentOperationPage={productOperationsList.newProduct}
                />
              }
            />
            <Route
              path={routeLinks.projectManagementProduct + "/:productId/preview"}
              element={
                <ProductIndex
                  currentOperationPage={productOperationsList.previewProduct}
                />
              }
            />
            <Route
              path={routeLinks.projectManagementProduct + "/task"}
              element={
                <ProductIndex
                  currentOperationPage={productOperationsList.productTask}
                />
              }
            />
            <Route
              path={routeLinks.projectManagementProduct + "/assign-pmc"}
              element={
                <ProductIndex
                  currentOperationPage={
                    productOperationsList.productAssignToPmc
                  }
                />
              }
            />
            <Route
              path={routeLinks.projectManagementProduct + "/timeline"}
              element={
                <ProductIndex
                  currentOperationPage={productOperationsList.productTimeline}
                />
              }
            />
            <Route
              path={
                routeLinks.projectManagementProduct +
                "/:projectId/:id/pmc-checklist"
              }
              element={
                <ProductIndex
                  currentOperationPage={
                    productOperationsList.productPMCHandover
                  }
                />
              }
            />
            <Route
              path={
                routeLinks.projectManagementProduct +
                "/:projectId/:id/execution-timeline"
              }
              element={
                <ProductIndex
                  currentOperationPage={
                    productOperationsList.productExecutionTimeline
                  }
                />
              }
            />
            <Route
              path={
                routeLinks.projectManagementProduct +
                "/:projectId/:id/edit-execution-timeline"
              }
              element={
                <ProductIndex
                  currentOperationPage={
                    productOperationsList.editProductExecutionTimeline
                  }
                />
              }
            />
            <Route
              path={routeLinks.projectManagementProduct + "/:projectId/:id/io"}
              element={
                <ProductIndex
                  currentOperationPage={
                    productOperationsList.productInputOutputDetails
                  }
                />
              }
            />
            <Route
              path={
                routeLinks.projectManagementProduct +
                "/:projectId/:id/handover-checklist"
              }
              element={
                <ProductIndex
                  currentOperationPage={
                    productOperationsList.pmcHandoverChecklistPreview
                  }
                />
              }
            />
            <Route
              path={
                routeLinks.projectManagementProduct +
                "/:projectId/:id/preview-execution-timeline"
              }
              element={
                <ProductIndex
                  currentOperationPage={
                    productOperationsList.executionTimelinePreview
                  }
                />
              }
            />
          </Route>
        </Route>
        <Route path="/500" element={<SomethingWentWrong />} />
        <Route path="/404" element={<PageNotFound />} />
        <Route path="/403" element={<Forbidden />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
